import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/components/assist/lib/utils';
import {
  ClipboardList,
  Users2,
  Heart,
  LayoutDashboard,
  CreditCard,
  FileText,
  Settings,
  TrendingUp,
  Sprout,
  Rocket,
} from 'lucide-react';

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/assist/ui/sidebar';
import { Separator } from '@/components/assist/ui/separator';
import { useInsightsNav } from '@/contexts/InsightsNavContext';
import { useFeatures } from '@/contexts/FeaturesContext';
import { useOnboardingState } from '@/hooks/useOnboardingState';
import { isFacialScanEnabled } from '../../utils/actionCardHelpers';
import { useGrowthPlan } from '../../features/selfcare/growth/hooks/useGrowthPlan';
import { isSegmentable } from '@/lib/tenantCapabilities';
import { INSIGHTS_NAV_SECTIONS, type InsightsNavItemDef } from './insightsNavigation';

// ---- Types ----

type Persona = 'insights' | 'self-care' | 'admin';

interface NavItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number;
  disabled?: boolean;
}

interface NavSection {
  /** i18n key for the section header. */
  label: string;
  items: NavItem[];
}

interface PersonaConfig {
  mainItems: NavItem[];
  bottomItems: NavItem[];
  subtitle: string;
  /**
   * When present, the persona renders as labelled sections instead of a flat
   * `mainItems` list. Used by the Insights persona (see insightsNavigation.ts).
   */
  sections?: NavSection[];
}

/**
 * Turn a declarative Insights menu item (insightsNavigation.ts) into a
 * render-ready NavItem by resolving its gate against live survey state.
 * Items flagged `notImplemented` are filtered out before this runs (they are
 * hidden from the live menu until their feature ships).
 */
/**
 * Whether an item declaring `hideWhenGated` should be dropped from the menu.
 *
 * Defaults to KEEPING the item while signals are still loading or if the
 * availability fetch failed, matching `surveyHasDrivers`'s own defensive
 * default — a menu entry must never disappear because a request was slow.
 */
function isHiddenByGate(
  def: InsightsNavItemDef,
  gates: { segmentable: boolean },
): boolean {
  if (!def.hideWhenGated) return false;
  if (def.gate === 'segmentable') return !gates.segmentable;
  return false;
}

function resolveInsightsItem(
  def: InsightsNavItemDef,
  gates: {
    hasCompletedSurvey: boolean;
    hasActiveSurvey: boolean;
    hasScheduledOrLiveSurvey: boolean;
    segmentable: boolean;
  },
): NavItem {
  let disabled = false;
  if (def.gate === 'segmentable') disabled = !gates.segmentable;
  else if (def.gate === 'completedSurvey') disabled = !gates.hasCompletedSurvey;
  else if (def.gate === 'activeSurvey') disabled = !gates.hasActiveSurvey;
  else if (def.gate === 'completedOrLaunchingSurvey') {
    disabled = !(gates.hasCompletedSurvey || gates.hasScheduledOrLiveSurvey);
  }
  return { title: def.titleKey, url: def.url, icon: def.icon, disabled };
}

// ---- Persona detection ----

function getPersona(pathname: string): Persona | null {
  if (pathname.startsWith('/insights')) return 'insights';
  if (pathname.startsWith('/self-care')) return 'self-care';
  if (pathname.startsWith('/admin')) return 'admin';
  return null;
}

// ---- Active-state ----

/**
 * Whether a sidebar item is the active one for the current location. Every
 * sidebar item now matches on pathname alone — `location.pathname` never
 * contains the query string, and no remaining item disambiguates by `?tab=`.
 */
function isNavItemActive(url: string, pathname: string): boolean {
  const [path] = url.split('?');
  return pathname === path;
}

// ---- Nav items per persona ----

function getPersonaConfigs(options: {
  hasCompletedSurvey: boolean;
  hasActiveSurvey: boolean;
  hasScheduledOrLiveSurvey: boolean;
  hasGrowthPlan: boolean;
  segmentable: boolean;
}): Record<Persona, PersonaConfig> {
  const {
    hasCompletedSurvey,
    hasActiveSurvey,
    hasScheduledOrLiveSurvey,
    hasGrowthPlan,
    segmentable,
  } = options;

  return {
    insights: {
      // The Insights menu is defined declaratively in insightsNavigation.ts
      // (the single source of truth that mirrors the product mockup). We only
      // resolve runtime gating here. See ./CLAUDE.md.
      mainItems: [],
      bottomItems: [],
      subtitle: 'sidebar.workforceIntelligence',
      // Items whose feature isn't built yet (`notImplemented`) are hidden, and
      // a section left with no items is dropped entirely. Removing the flag
      // (once the route exists) makes the item reappear in place.
      sections: INSIGHTS_NAV_SECTIONS.map((section) => ({
        label: section.labelKey,
        items: section.items
          .filter((def) => !def.notImplemented)
          .filter((def) => !isHiddenByGate(def, { segmentable }))
          .map((def) =>
            resolveInsightsItem(def, {
              hasCompletedSurvey,
              hasActiveSurvey,
              hasScheduledOrLiveSurvey,
              segmentable,
            }),
          ),
      })).filter((section) => section.items.length > 0),
    },
    'self-care': {
      mainItems: [
        { title: 'sidebar.myWellbeing', url: '/self-care', icon: Heart },
        { title: 'sidebar.trends', url: '/self-care/trends', icon: TrendingUp, disabled: false },
        ...(hasGrowthPlan
          ? [
              {
                title: 'sidebar.growthPlan',
                url: '/self-care/growth-plan',
                icon: Sprout,
              } as NavItem,
            ]
          : []),
      ],
      bottomItems: [],
      subtitle: 'sidebar.yourHealthJourney',
    },
    admin: {
      mainItems: [
        { title: 'sidebar.overview', url: '/admin', icon: LayoutDashboard },
        { title: 'sidebar.users', url: '/admin/users', icon: Users2 },
        { title: 'sidebar.surveys', url: '/admin/surveys', icon: ClipboardList },
        { title: 'sidebar.subscription', url: '/admin/subscription', icon: CreditCard },
        { title: 'sidebar.audit', url: '/admin/audit', icon: FileText },
      ],
      bottomItems: [
        { title: 'sidebar.settings', url: '/admin/settings', icon: Settings },
      ],
      subtitle: 'sidebar.administration',
    },
  };
}

// ---- Component ----

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const location = useLocation();
  const { t, i18n } = useTranslation('assist');

  const persona = getPersona(location.pathname);
  const {
    hasActiveSurvey,
    hasCompletedSurvey,
    scheduledOrLiveSurvey,
    surveyHasDrivers,
    surveyHasKpis,
    loading,
  } = useInsightsNav();
  // Shared with the overview's action board (EL-1175) — see
  // `lib/tenantCapabilities`. Both flags default to true while availability is
  // loading or if it failed, so the entry never vanishes on a slow request.
  const segmentable = isSegmentable({ surveyHasDrivers, surveyHasKpis });
  const { features, loading: loadingFeatures } = useFeatures();
  const growthPlan = useGrowthPlan(i18n.language);
  // The OnboardingStateProvider only mounts inside InsightsDashboard (below
  // the router), so the shared sidebar can't read it. Fetch onboarding state
  // directly instead, enabled only for the insights persona so other personas
  // never hit the endpoint. Shows the "Get started → Onboarding" entry until
  // the tenant launches its first survey.
  const onboarding = useOnboardingState(persona === 'insights');
  const showOnboardingEntry =
    persona === 'insights' &&
    onboarding.state != null &&
    !onboarding.state.is_onboarded &&
    !onboarding.state.onboarding_flow_disabled;

  if (!persona) {
    return null;
  }

  const configs = getPersonaConfigs({
    hasCompletedSurvey: persona === 'insights' ? hasCompletedSurvey && !loading : true,
    hasActiveSurvey: persona === 'insights' ? hasActiveSurvey && !loading : true,
    hasScheduledOrLiveSurvey: persona === 'insights' ? !!scheduledOrLiveSurvey && !loading : true,
    hasGrowthPlan: persona === 'self-care' && growthPlan.state === 'ready',
    segmentable,
  });
  const config = configs[persona];

  const mainItems = persona === 'self-care' && !loadingFeatures && !isFacialScanEnabled(features)
    ? config.mainItems.filter(item => item.url !== '/self-care/biomarkers')
    : config.mainItems;

  const renderNavItem = (item: NavItem) => {
    const title = t(item.title);
    const isActive = isNavItemActive(item.url, location.pathname);
    const Icon = item.icon;
    // Absolute URLs (https://…) can't go through react-router, so they are
    // rendered as a plain external <a> below. Pages served by THIS app must
    // use a relative path (see insightsNavigation.ts) so they route on every
    // environment — never hard-code a domain.
    const isExternal = /^https?:\/\//.test(item.url);

    if (item.disabled) {
      return (
        <span
          className={cn(
            'flex items-center gap-3 rounded-lg px-3 py-2 w-full relative',
            'text-sidebar-foreground/40 cursor-not-allowed'
          )}
        >
          <div className="relative shrink-0">
            <Icon className="w-5 h-5" />
          </div>
          {!collapsed && <span className="flex-1">{title}</span>}
        </span>
      );
    }

    const itemClassName = cn(
      'flex items-center gap-3 rounded-lg px-3 py-2 transition-colors w-full relative',
      isActive
        // Active uses the same white/10 background as hover, so the selected
        // item is visible on page load. The `!` (important) is required: this
        // link is wrapped by the shadcn SidebarMenuButton, which applies
        // `data-[active=true]:bg-sidebar-accent` — transparent here, since
        // `--sidebar-accent` is white/0 — and that attribute selector outranks
        // a plain bg utility, otherwise erasing this background.
        ? 'bg-white/10! text-sidebar-foreground font-medium'
        : 'text-sidebar-foreground/80 hover:bg-white/10 hover:text-sidebar-foreground'
    );

    const inner = (
      <>
        <div className="relative shrink-0">
          <Icon className="w-5 h-5" />
          {collapsed && item.badge && item.badge > 0 && (
            <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-semibold text-primary-foreground">
              {item.badge}
            </span>
          )}
        </div>
        {!collapsed && (
          <>
            <span className="flex-1">{title}</span>
            {item.badge && item.badge > 0 && (
              <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">
                {item.badge}
              </span>
            )}
          </>
        )}
      </>
    );

    if (isExternal) {
      return (
        <a href={item.url} className={itemClassName}>
          {inner}
        </a>
      );
    }

    return (
      <NavLink to={item.url} className={itemClassName}>
        {inner}
      </NavLink>
    );
  };

  return (
    <Sidebar collapsible="icon" className="border-r-0 bg-sidebar">
      <SidebarHeader className="p-4">
        <div className="flex items-center gap-3">
          {!collapsed ? (
            <div className="overflow-hidden">
              <div className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 92 92" className="h-9 w-9" aria-hidden="true">
                  <rect width="92" height="92" rx="20" fill="#510CFF" />
                  <circle cx="32" cy="32" r="13" fill="#20C279" />
                  <circle cx="60" cy="32" r="11" fill="#FAFAF4" />
                  <circle cx="32" cy="60" r="12" fill="#C8C9FC" />
                </svg>
                <span className="text-[26px] font-extrabold tracking-[-0.04em] leading-none text-sidebar-foreground">
                  elli
                </span>
              </div>
              <p className="text-xs text-sidebar-foreground/70 mt-1">
                {t(config.subtitle)}
              </p>
            </div>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 92 92" className="h-6 w-6" aria-hidden="true">
              <rect width="92" height="92" rx="20" fill="#510CFF" />
              <circle cx="32" cy="32" r="13" fill="#20C279" />
              <circle cx="60" cy="32" r="11" fill="#FAFAF4" />
              <circle cx="32" cy="60" r="12" fill="#C8C9FC" />
            </svg>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="flex flex-col">
        {showOnboardingEntry && (
          <SidebarGroup data-testid="sidebar-onboarding-group">
            {!collapsed && (
              <SidebarGroupLabel className="text-sidebar-foreground/60">
                {t('sidebar.getStarted')}
              </SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    tooltip={t('sidebar.onboarding')}
                    isActive={location.pathname === '/insights'}
                  >
                    {renderNavItem({
                      title: 'sidebar.onboarding',
                      url: '/insights',
                      icon: Rocket,
                    })}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {config.sections ? (
          config.sections.map((section) => (
            <SidebarGroup key={section.label} data-testid={`sidebar-section-${section.label}`}>
              {!collapsed && (
                <SidebarGroupLabel className="text-sidebar-foreground/60">
                  {t(section.label)}
                </SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu>
                  {section.items.map((item) => (
                    <SidebarMenuItem key={item.url}>
                      <SidebarMenuButton
                        asChild
                        tooltip={t(item.title)}
                        isActive={!item.disabled && isNavItemActive(item.url, location.pathname)}
                      >
                        {renderNavItem(item)}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))
        ) : (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {mainItems.map((item) => (
                  <SidebarMenuItem key={item.url}>
                    <SidebarMenuButton
                      asChild
                      tooltip={t(item.title)}
                      isActive={!item.disabled && location.pathname === item.url}
                    >
                      {renderNavItem(item)}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {config.bottomItems.length > 0 && (
          <div className="mt-auto">
            <Separator className="my-2 bg-white/20" />
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  {config.bottomItems.map((item) => (
                    <SidebarMenuItem key={item.url}>
                      <SidebarMenuButton
                        asChild
                        tooltip={t(item.title)}
                        isActive={location.pathname.startsWith(item.url)}
                      >
                        {renderNavItem(item)}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </div>
        )}
      </SidebarContent>
    </Sidebar>
  );
}
